// src/redux/profiles/profileMigrationSlice.ts

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import type {
  CurrentProfileResponse,
  MigrateProfilePayload,
  MigrateProfileResponse,
  MigrationHistoryItem,
  MigrationHistoryResponse,
  ProfileMigrationStatusResponse,
} from "../../types/profiles/profileMigrationTypes";

import {
  fetchCurrentProfileApi,
  fetchProfileMigrationHistoryApi,
  fetchProfileMigrationStatusApi,
  migrateProfileApi,
} from "../../services/profiles/profileMigrationApi";

import { fetchAuthMe } from "../auth/authSlice";

interface ProfileMigrationState {
  status: ProfileMigrationStatusResponse | null;
  currentProfile: CurrentProfileResponse | null;
  history: MigrationHistoryItem[];
  loading: boolean;
  error: string | Record<string, any> | null;
  successMessage: string | null;
}

const initialState: ProfileMigrationState = {
  status: null,
  currentProfile: null,
  history: [],
  loading: false,
  error: null,
  successMessage: null,
};

// ---------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------
export const getProfileMigrationStatus = createAsyncThunk<
  ProfileMigrationStatusResponse,
  void,
  { rejectValue: string | Record<string, any> }
>(
  "profileMigration/getProfileMigrationStatus",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchProfileMigrationStatusApi();
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch migration status."
      );
    }
  }
);

export const getCurrentProfileMeta = createAsyncThunk<
  CurrentProfileResponse,
  void,
  { rejectValue: string | Record<string, any> }
>(
  "profileMigration/getCurrentProfileMeta",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchCurrentProfileApi();
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch current profile metadata."
      );
    }
  }
);

export const getProfileMigrationHistory = createAsyncThunk<
  MigrationHistoryResponse,
  void,
  { rejectValue: string | Record<string, any> }
>(
  "profileMigration/getProfileMigrationHistory",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchProfileMigrationHistoryApi();
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch migration history."
      );
    }
  }
);

export const migrateProfile = createAsyncThunk<
  MigrateProfileResponse,
  MigrateProfilePayload,
  { rejectValue: string | Record<string, any> }
>(
  "profileMigration/migrateProfile",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const data = await migrateProfileApi(payload);

      // Sync auth/user flags like is_member and active profile
      await dispatch(fetchAuthMe()).unwrap();

      // Refresh migration state
      await dispatch(getCurrentProfileMeta()).unwrap();
      await dispatch(getProfileMigrationStatus()).unwrap();
      await dispatch(getProfileMigrationHistory()).unwrap();

      return data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to migrate profile."
      );
    }
  }
);

// ---------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------
const profileMigrationSlice = createSlice({
  name: "profileMigration",
  initialState,
  reducers: {
    clearProfileMigrationState(state) {
      state.status = null;
      state.currentProfile = null;
      state.history = [];
      state.loading = false;
      state.error = null;
      state.successMessage = null;
    },
    clearProfileMigrationError(state) {
      state.error = null;
    },
    clearProfileMigrationSuccess(state) {
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // -------------------------------------------------
      // Status
      // -------------------------------------------------
      .addCase(getProfileMigrationStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProfileMigrationStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.status = action.payload;
      })
      .addCase(getProfileMigrationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // -------------------------------------------------
      // Current profile meta
      // -------------------------------------------------
      .addCase(getCurrentProfileMeta.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentProfileMeta.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProfile = action.payload;
      })
      .addCase(getCurrentProfileMeta.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // -------------------------------------------------
      // History
      // -------------------------------------------------
      .addCase(getProfileMigrationHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProfileMigrationHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.history = action.payload.results || [];
      })
      .addCase(getProfileMigrationHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // -------------------------------------------------
      // Migrate
      // -------------------------------------------------
      .addCase(migrateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(migrateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(migrateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearProfileMigrationState,
  clearProfileMigrationError,
  clearProfileMigrationSuccess,
} = profileMigrationSlice.actions;

export default profileMigrationSlice.reducer;